// Optimistic local application of the category-module operations (see apply.ts). Documents are written straight from the shared spec;
// ledger operations (wallet float, personal money, loans) write their rows and leave every balance to the overlay.
import { D, MODULE_ENTITIES, bizDate as bizOf, DOC_MODULES, type EntitySpec, type FieldSpec, type ModuleOpType } from '@dd/shared';
import type { ApplyCtx } from './ctx';
import type { ModuleRow, OutboxItem } from '@/db/types';

type Op = Pick<OutboxItem, 'opId' | 'entityId' | 'clientTs' | 'baseVersion' | 'actor' | 'changedFields'>;
type Handler = (c: ApplyCtx, p: Record<string, any>, op: Op) => Promise<void>;

const spec = (entity: string): EntitySpec => MODULE_ENTITIES.find((e) => e.entity === entity)!;

/** payload value → the form the server returns it in */
function wire(f: FieldSpec, v: unknown): unknown {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (f.type === 'money') return D(v as string).toFixed(2);
  if (f.type === 'qty') return D(v as string).toFixed(3);
  return v;
}

const docHandler = (s: EntitySpec): Handler => async (c, p, op) => {
  const ex = (await c.table(s.entity).get(op.entityId)) as ModuleRow | undefined;
  if (!ex && p.deleted) return;
  const row: ModuleRow = ex ? { ...ex } : { id: op.entityId, shopId: c.item.shopId, createdAt: op.clientTs, version: 0, deletedAt: null };
  const only = ex && op.changedFields?.length ? new Set(op.changedFields) : null;
  for (const f of s.fields) {
    if (f.server) continue;
    if (only && !only.has(f.key)) continue;
    const v = wire(f, p[f.key]);
    if (v !== undefined) row[f.key] = v;
  }
  if (!ex && s.entity === 'mfs_wallets') row.balance = D(p.opening ?? 0).toFixed(2);
  if (p.deleted) row.deletedAt = op.clientTs;
  row._pending = 1;
  await c.put(s.entity, row);
};

const ledgerRow = (entity: string, c: ApplyCtx, p: Record<string, any>, op: Op, extra: Record<string, unknown>): Promise<unknown> => {
  const s = spec(entity);
  const row: ModuleRow = { id: op.entityId, shopId: c.item.shopId, createdAt: op.clientTs, version: 0, voidedAt: null, deletedAt: null, _pending: 1 };
  for (const f of s.fields) if (!f.server) { const v = wire(f, p[f.key]); if (v !== undefined) row[f.key] = v; }
  return c.put(entity, { ...row, ...extra });
};
const voidRow = (entity: string): Handler => async (c, _p, op) => { await c.patch(entity, op.entityId, { voidedAt: op.clientTs, _pending: 1 }); };

export const MODULE_APPLY = {
  ...Object.fromEntries(DOC_MODULES.map((s) => [s.op, docHandler(s)])),
  'mfs_txn.create': async (c, p, op) => { await ledgerRow('mfs_txns', c, p, op, { bizDate: bizOf(p.occurredAt) }); },
  'mfs_txn.void': voidRow('mfs_txns'),
  'recharge.create': async (c, p, op) => { await ledgerRow('recharges', c, p, op, { bizDate: bizOf(p.occurredAt) }); },
  'recharge.void': voidRow('recharges'),
  'personal_entry.create': async (c, p, op) => { await ledgerRow('personal_entries', c, p, op, { bizDate: p.bizDate ?? c.bizDate }); },
  'personal_entry.void': voidRow('personal_entries'),
  'loan.create': async (c, p, op) => { await ledgerRow('loans', c, p, op, { balance: D(p.amount).toFixed(2), status: 'open', bizDate: c.bizDate }); },
  'loan.repay': async (c, p, op) => {
    const loan = (await c.table('loans').get(p.loanId)) as ModuleRow | undefined;
    await ledgerRow('loan_repayments', c, p, op, { moneyAccountId: p.moneyAccountId ?? loan?.moneyAccountId ?? null, bizDate: c.bizDate });
  },
  // ── v10 corrections: rows change here, every balance comes from the overlay (it reads the `undo` snapshots for "before") ──
  'personal_entry.edit': async (c, p, op) => {
    const old = (await c.table('personal_entries').get(op.entityId)) as ModuleRow | undefined;
    if (old && !old.voidedAt) await c.patch('personal_entries', old.id, { voidedAt: op.clientTs, replacedById: p.newId, _pending: 1 });
    const { newId, ...entry } = p;
    await ledgerRow('personal_entries', c, entry, { ...op, entityId: newId }, { bizDate: p.bizDate ?? c.bizDate });
  },
  'personal_entry.restore': async (c, _p, op) => { await c.patch('personal_entries', op.entityId, { voidedAt: null, _pending: 1 }); },
  'loan.update': async (c, p, op) => {
    const patch: Record<string, unknown> = { party: p.party, amount: D(p.amount).toFixed(2), _pending: 1 };
    // like the server: a field the edit did not send keeps its value
    if (p.partyPhone !== undefined) patch.partyPhone = p.partyPhone;
    if (p.moneyAccountId !== undefined) patch.moneyAccountId = p.moneyAccountId;
    if (p.dueDate !== undefined) patch.dueDate = p.dueDate;
    if (p.note !== undefined) patch.note = p.note;
    await c.patch('loans', op.entityId, patch);
  },
  'loan.delete': async (c, _p, op) => {
    const loan = await c.patch('loans', op.entityId, { deletedAt: op.clientTs, _pending: 1 });
    if (!loan) return;
    for (const r of (await c.table('loan_repayments').where('loanId').equals(op.entityId).toArray()) as ModuleRow[]) if (!r.deletedAt) await c.patch('loan_repayments', r.id, { deletedAt: op.clientTs });
  },
  'loan.restore': async (c, _p, op) => {
    const loan = (await c.table('loans').get(op.entityId)) as ModuleRow | undefined;
    if (!loan?.deletedAt) return;
    for (const r of (await c.table('loan_repayments').where('loanId').equals(op.entityId).toArray()) as ModuleRow[]) if (r.deletedAt === loan.deletedAt) await c.patch('loan_repayments', r.id, { deletedAt: null });
    await c.patch('loans', op.entityId, { deletedAt: null, _pending: 1 });
  },
  'loan_repayment.void': async (c, _p, op) => { await c.patch('loan_repayments', op.entityId, { deletedAt: op.clientTs, _pending: 1 }); },
  'savings_goal.contribute': async (c, p, op) => {
    const g = (await c.table('savings_goals').get(op.entityId)) as ModuleRow | undefined;
    if (!g) return;
    const next = p.kind === 'deposit' ? D(g.savedAmount ?? 0).plus(p.amount) : D(g.savedAmount ?? 0).minus(p.amount);
    const status = g.status === 'cancelled' ? 'cancelled' : next.gte(g.targetAmount ?? 0) ? 'done' : 'active';
    await c.patch('savings_goals', g.id, { savedAmount: next.toFixed(2), status, _pending: 1 });
  },
} as Record<ModuleOpType, Handler>;

/** op type → the module table whose pulled rows must not overwrite an unacknowledged local edit */
export const MODULE_GUARDS: Record<string, string> = {
  ...Object.fromEntries(DOC_MODULES.map((s) => [s.op, s.entity])),
  'mfs_txn.create': 'mfs_txns', 'mfs_txn.void': 'mfs_txns', 'recharge.create': 'recharges', 'recharge.void': 'recharges',
  'personal_entry.create': 'personal_entries', 'personal_entry.void': 'personal_entries', 'loan.create': 'loans', 'loan.repay': 'loan_repayments',
  'personal_entry.edit': 'personal_entries', 'personal_entry.restore': 'personal_entries', 'loan.update': 'loans', 'loan.delete': 'loans', 'loan.restore': 'loans',
  'loan_repayment.void': 'loan_repayments', 'savings_goal.contribute': 'savings_goals',
};
